<script lang="ts" setup>
import RichTextField from '@/components/common/RichTextField.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { GoalsActions, GoalsState } from '@/stores/goals.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import CategoryField from '../categories/CategoryField.vue';

import {
  descriptionRules,
  Goal,
  GoalItem,
  titleRules,
  defaultGoal,
} from '@/models/goal.model';
import GoalItems from './GoalItems.vue';

const store = useStore<ShiftCMSState & { ev: GoalsState }>();

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<Goal>,
  },
});

const valid = ref<boolean>(false);

const goal = ref<Goal>({ ...defaultGoal });

const currentValue = ref<Goal>({ ...defaultGoal });

const action = computed<string>(() =>
  goal.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveGoal = async (e: unknown, close = true) => {
  let success: boolean = false;
  if (valid.value) {
    if (goal.value.id === NEW_ID) {
      success = await store.dispatch(GoalsActions.AddGoal, goal.value);
    } else {
      success = await store.dispatch(GoalsActions.UpdateGoal, goal.value);
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Goal',
      type: 'success',
    });

    if (close) {
      store.commit(Mutations.HideDrawer);
      store.dispatch(GoalsActions.GetGoals);
    }
    currentValue.value = { ...goal.value };
    store.commit(Mutations.SetDirtyState, false);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Goal',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (goal.value.id === NEW_ID) {
    Object.assign(goal, { title: '' });
  } else {
    const item = await store.dispatch(GoalsActions.GetGoal, goal.value.id);

    if (item) {
      Object.assign(goal.value, {
        ...item,
        startDate: new Date(item.startDate || new Date().toISOString()),
        endDate: new Date(item.endDate || new Date().toISOString()),
      });

      currentValue.value = { ...goal.value };
    }
  }
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);

  if (props.data) {
    Object.assign(goal.value, {
      ...props.data,
    });

    currentValue.value = { ...goal.value };
  }
});

const removeLabel = async () => {
  if (
    goal.value &&
    goal.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Goal"?')
  ) {
    store.dispatch(GoalsActions.DeleteGoal, goal.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const togglePublished = async (published: boolean) => {
  if (goal.value.id !== -1) {
    store.dispatch(GoalsActions.PublishGoal, {
      id: goal.value.id,
      published,
    });
  }
  goal.value.published = published;
};

function updateItems(items: GoalItem[]) {
  goal.value.items = items;
}

function getSimpleValue(i: Goal) {
  return {
    title: i.title,
    subTitle: i.subTitle,
    description: i.description,
    url: i.description,
    listTitle: i.listTitle,
    items: JSON.stringify(i.items),
  };
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(goal.value)) !==
    JSON.stringify(getSimpleValue(currentValue.value));

  store.commit(Mutations.SetDirtyState, isDirty);

  return isDirty;
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveGoal">
    <v-container>
      <h2>
        {{ $t(action) }} a goal <span v-if="isDirty">*</span>
        <CurrentLanguage />
      </h2>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="goal.title"
            :rules="titleRules"
            label="Title"
            hint="Used on goal page and in lists"
            required
          ></v-text-field>
        </v-col>
        <v-col class="pt-6">
          <v-btn
            block
            :disabled="goal.id === NEW_ID"
            @click="() => togglePublished(!goal.published)"
            :color="!goal.published ? 'success' : 'warning'"
            >{{ goal.published ? 'Un-publish' : 'Publish' }}</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="goal.subTitle"
            :rules="titleRules"
            label="Subtitle"
            hint="Used on goal page"
            required
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            v-model="goal.userGoalsCount"
            label="Favourite count"
            density="compact"
          />
        </v-col>
      </v-row>
      <CategoryField main v-model="goal.mainCategory" label="Main Category" />
      <CategoryField
        :sub="goal.mainCategory"
        v-model="goal.subCategory"
        label="Sub Category"
      />

      <RichTextField
        label="Description"
        :height="200"
        :rules="descriptionRules"
        v-model="goal.description"
        :media="[]"
      />
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="goal.listTitle"
            :rules="titleRules"
            label="List title"
            hint="Shown above the list of items"
            required
          ></v-text-field>
        </v-col>
        <v-col> </v-col>
      </v-row>
      <GoalItems :items="goal.items" @update="updateItems" :goal="goal" />
      <!-- <CategoriesField v-model="goal.categories" /> -->

      <v-row>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLabel"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            @click="saveGoal($event, false)"
            color="success"
            block
            >{{ action }} <span v-if="isDirty">*</span></v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block
            >{{ action }} and close <span v-if="isDirty">*</span></v-btn
          ></v-col
        >
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
